import { friendlyDbError } from '../utils/friendlyDbError';
import { create } from 'zustand';
import { supabase } from '../supabase';
import type { DealVariable, DealVariableFormData } from '../types';
import { getUserData } from '../utils';

interface DealVariableStore {
  variables: DealVariable[];
  loading: boolean;
  error: string | null;

  fetchServiceVariables: (dealServiceId: string) => Promise<void>;
  createServiceVariable: (dealServiceId: string, variable: DealVariableFormData) => Promise<DealVariable | null>;
  updateServiceVariable: (id: string, variable: Partial<DealVariableFormData>) => Promise<DealVariable | null>;
  deleteServiceVariable: (id: string) => Promise<void>;
  reorderServiceVariables: (dealServiceId: string, variableIds: string[]) => Promise<void>;
  confirmVariable: (id: string, calculatedAmount?: number) => Promise<DealVariable | null>;
  unconfirmVariable: (id: string) => Promise<DealVariable | null>;
  createActivityNote: (variable: DealVariable, action: 'confirmed' | 'unconfirmed', calculatedAmount?: number) => Promise<void>;

  // Utility methods
  getServiceVariablesByServiceId: (dealServiceId: string) => DealVariable[];
  calculateServiceVariablesTotal: (dealServiceId: string, serviceTotal: number) => { variablesTotal: number; costsTotal: number; grandTotal: number; };
}

export const useDealVariableStore = create<DealVariableStore>((set, get) => ({
  variables: [],
  loading: false,
  error: null,

  fetchServiceVariables: async (dealServiceId: string) => {
    set({ loading: true, error: null });
    
    try {
      const userData = await getUserData();
      if (!userData?.organizationId) {
        console.error('User data missing organizationId:', userData);
        throw new Error('Organization not found');
      }

      const { data, error } = await supabase
        .from('deal_variables')
        .select('*')
        .eq('deal_service_id', dealServiceId)
        .eq('organization_id', userData.organizationId)
        .order('sort_order', { ascending: true });

      if (error) throw error;

      // Instead of replacing all variables, merge with existing ones
      set(state => {
        // Remove existing variables for this service
        const otherServiceVariables = state.variables.filter(v => v.deal_service_id !== dealServiceId);
        // Add the new variables for this service
        return {
          variables: [...otherServiceVariables, ...(data || [])],
          loading: false
        };
      });
    } catch (error) {
      console.error('Error fetching service variables:', error);
      set({ error: friendlyDbError(error) || 'Failed to fetch service variables', loading: false });
    }
  },

  createServiceVariable: async (dealServiceId: string, variable: DealVariableFormData) => {
    set({ loading: true, error: null });
    
    try {
      console.log('🔧 createServiceVariable called with:', { dealServiceId, variable });
      
      const userData = await getUserData();
      console.log('👤 User data:', userData);
      
      if (!userData?.organizationId) {
        console.error('User data missing organizationId:', userData);
        throw new Error('Organization not found');
      }

      // Get the current max sort_order for this service
      const { data: existingVariables } = await supabase
        .from('deal_variables')
        .select('sort_order')
        .eq('deal_service_id', dealServiceId)
        .eq('organization_id', userData.organizationId)
        .order('sort_order', { ascending: false })
        .limit(1);

      const nextSortOrder = existingVariables && existingVariables.length > 0 
        ? existingVariables[0].sort_order + 1 
        : 0;

      console.log('📊 Next sort order:', nextSortOrder);

      const insertData = {
        ...variable,
        deal_service_id: dealServiceId,
        organization_id: userData.organizationId,
        sort_order: nextSortOrder,
        created_by: userData.userId,
        updated_by: userData.userId,
        base_amount: variable.base_amount || null,
        is_internal_cost: variable.is_internal_cost ?? false  // Default: false (fatturabile)
      };

      console.log('📝 Insert data:', insertData);

      const { data, error } = await supabase
        .from('deal_variables')
        .insert([insertData])
        .select()
        .single();

      console.log('💾 Insert result:', { data, error });

      if (error) throw error;

      // Update local state
      set(state => ({
        variables: [...state.variables, data],
        loading: false
      }));

      console.log('✅ Variable created successfully:', data);
      return data;
    } catch (error) {
      console.error('❌ Error creating service variable:', error);
      set({ error: friendlyDbError(error) || 'Failed to create service variable', loading: false });
      return null;
    }
  },

  updateServiceVariable: async (id: string, variable: Partial<DealVariableFormData>) => {
    set({ loading: true, error: null });

    try {
      console.log('📝 updateServiceVariable called with id:', id);
      console.log('📝 Variable data to update:', variable);
      console.log('📝 is_stamp_duty_art15 in variable:', variable.is_stamp_duty_art15);

      const userData = await getUserData();
      if (!userData?.organizationId) {
        throw new Error('Organization not found');
      }

      const updatePayload = {
        ...variable,
        updated_by: userData.userId,
        updated_at: new Date().toISOString()
      };
      console.log('📝 Full update payload:', updatePayload);

      const { data, error } = await supabase
        .from('deal_variables')
        .update(updatePayload)
        .eq('id', id)
        .eq('organization_id', userData.organizationId)
        .select()
        .single();

      console.log('📝 Supabase update result:', { data, error });
      console.log('📝 Returned is_stamp_duty_art15:', data?.is_stamp_duty_art15);

      if (error) throw error;

      // Update local state
      set(state => ({
        variables: state.variables.map(v => v.id === id ? data : v),
        loading: false
      }));

      return data;
    } catch (error) {
      console.error('Error updating service variable:', error);
      set({ error: friendlyDbError(error) || 'Failed to update service variable', loading: false });
      return null;
    }
  },

  deleteServiceVariable: async (id: string) => {
    set({ loading: true, error: null });
    
    try {
      const userData = await getUserData();
      if (!userData?.organizationId) {
        throw new Error('Organization not found');
      }

      const { error } = await supabase
        .from('deal_variables')
        .delete()
        .eq('id', id)
        .eq('organization_id', userData.organizationId);

      if (error) throw error;

      // Update local state
      set(state => ({
        variables: state.variables.filter(v => v.id !== id),
        loading: false
      }));
    } catch (error) {
      console.error('Error deleting service variable:', error);
      set({ error: friendlyDbError(error) || 'Failed to delete service variable', loading: false });
    }
  },

  reorderServiceVariables: async (dealServiceId: string, variableIds: string[]) => {
    set({ loading: true, error: null });

    try {
      const userData = await getUserData();
      if (!userData?.organizationId) {
        throw new Error('Organization not found');
      }

      // Update sort orders
      const updates = variableIds.map((id, index) => ({
        id,
        sort_order: index,
        updated_by: userData.id,
        updated_at: new Date().toISOString()
      }));

      const { error } = await supabase
        .from('deal_variables')
        .upsert(updates, { onConflict: 'id' });

      if (error) throw error;

      // Refresh variables
      await get().fetchServiceVariables(dealServiceId);
    } catch (error) {
      console.error('Error reordering service variables:', error);
      set({ error: friendlyDbError(error) || 'Failed to reorder service variables', loading: false });
    }
  },

  confirmVariable: async (id: string, calculatedAmount?: number) => {
    set({ loading: true, error: null });

    try {
      const userData = await getUserData();
      if (!userData?.organizationId) {
        throw new Error('Organization not found');
      }

      const { data, error } = await supabase
        .from('deal_variables')
        .update({
          is_confirmed: true,
          confirmed_at: new Date().toISOString(),
          confirmed_by: userData.userId,
          updated_by: userData.userId,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .eq('organization_id', userData.organizationId)
        .select()
        .single();

      if (error) throw error;

      // Create activity note for confirmation
      await get().createActivityNote(data, 'confirmed', calculatedAmount);

      // Update local state
      set(state => ({
        variables: state.variables.map(v => v.id === id ? data : v),
        loading: false
      }));

      return data;
    } catch (error) {
      console.error('Error confirming variable:', error);
      set({ error: friendlyDbError(error) || 'Failed to confirm variable', loading: false });
      return null;
    }
  },

  unconfirmVariable: async (id: string) => {
    set({ loading: true, error: null });

    try {
      const userData = await getUserData();
      if (!userData?.organizationId) {
        throw new Error('Organization not found');
      }

      const { data, error } = await supabase
        .from('deal_variables')
        .update({
          is_confirmed: false,
          confirmed_at: null,
          confirmed_by: null,
          updated_by: userData.userId,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .eq('organization_id', userData.organizationId)
        .select()
        .single();

      if (error) throw error;

      // Create activity note for unconfirmation
      await get().createActivityNote(data, 'unconfirmed');

      // Update local state
      set(state => ({
        variables: state.variables.map(v => v.id === id ? data : v),
        loading: false
      }));

      return data;
    } catch (error) {
      console.error('Error unconfirming variable:', error);
      set({ error: friendlyDbError(error) || 'Failed to unconfirm variable', loading: false });
      return null;
    }
  },

  createActivityNote: async (variable: DealVariable, action: 'confirmed' | 'unconfirmed', calculatedAmount?: number) => {
    try {
      const userData = await getUserData();
      if (!userData?.organizationId || !userData?.userId) {
        console.error('User data not available for creating activity note');
        return;
      }

      // Get deal_id from deal_service
      const { data: dealService, error: serviceError } = await supabase
        .from('deal_services')
        .select('deal_id')
        .eq('id', variable.deal_service_id)
        .single();

      if (serviceError || !dealService) {
        console.error('Error fetching deal service:', serviceError);
        return;
      }

      // Format variable details
      const variableType = variable.type === 'percentage' ? 'Percentuale' : 'Fisso';
      const amountDisplay = variable.type === 'percentage'
        ? `${variable.amount}%${variable.base_amount ? ` su €${variable.base_amount.toFixed(2)}` : ''}`
        : `€${variable.amount.toFixed(2)}`;

      const calculatedDisplay = calculatedAmount
        ? ` = €${calculatedAmount.toFixed(2)}`
        : '';

      const costLabel = variable.is_cost ? 'Costo' : 'Variabile';
      const vatLabel = variable.is_vat_exempt ? 'Esente IVA' : 'IVA applicabile';
      const stampDutyLabel = variable.is_stamp_duty ? ' (Marca da bollo)' : '';

      // Create note description
      const actionLabel = action === 'confirmed' ? 'confermata' : 'annullata';
      const description = `Variabile ${actionLabel}: **${variable.name}**\n\n` +
        `**Dettagli:**\n` +
        `- Tipo: ${costLabel}\n` +
        `- Importo: ${amountDisplay}${calculatedDisplay}\n` +
        `- Tipologia: ${variableType}\n` +
        `- IVA: ${vatLabel}${stampDutyLabel}\n` +
        (variable.description ? `- Descrizione: ${variable.description}\n` : '') +
        `\nAzione eseguita il ${new Date().toLocaleString('it-IT')}`;

      // Insert activity note
      await supabase
        .from('sales_activities')
        .insert({
          title: `Variabile ${actionLabel}: ${variable.name}`,
          description: description,
          type: 'note',
          status: 'completed',
          due_date: new Date().toISOString(),
          deal_id: dealService.deal_id,
          user_id: userData.userId,
          organization_id: userData.organizationId,
          assigned_to: userData.userId,
          completed_at: new Date().toISOString(),
          outcome: `Variabile ${variable.name} ${actionLabel} con successo`
        });

    } catch (error) {
      console.error('Error creating activity note:', error);
      // Don't throw - we don't want to fail the main operation if note creation fails
    }
  },

  getServiceVariablesByServiceId: (dealServiceId: string) => {
    return get().variables.filter(v => v.deal_service_id === dealServiceId);
  },

  calculateServiceVariablesTotal: (dealServiceId: string, serviceTotal: number) => {
    const variables = get().getServiceVariablesByServiceId(dealServiceId);

    let variablesTotal = 0;      // Variabili da aggiungere (marche da bollo, trasferte, etc.)
    let internalCostsTotal = 0;  // Costi interni da sottrarre (materiali, outsourcing, etc.)

    variables.forEach(variable => {
      let amount = variable.amount;

      // Calculate percentage-based amounts
      if (variable.type === 'percentage') {
        amount = (serviceTotal * variable.amount) / 100;
      }

      // NUOVA LOGICA: Usa is_internal_cost invece di is_cost
      // - is_internal_cost = true  → Costo interno (non fatturabile, sottratto dal margine)
      // - is_internal_cost = false → Variabile fatturabile (marche da bollo, etc., sommata al totale)
      if (variable.is_internal_cost === true) {
        internalCostsTotal += amount;
      } else {
        variablesTotal += amount;
      }
    });

    const grandTotal = serviceTotal + variablesTotal - internalCostsTotal;

    return {
      variablesTotal,
      costsTotal: internalCostsTotal,  // Nome campo rimane uguale per compatibilità
      grandTotal
    };
  }
}));